import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPowerOff, faUser, faUserXmark, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { hasImpersonationFunctionality, hasImpersonated, useAuth } from '../components/auth/AuthCheck';
import ImpersonationDialog from '../components/auth/ImpersonationDialog';
import StopImpersonationDialog from '../components/auth/StopImpersonationDialog';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
interface SystemConfig {
  Id: string;
  ConfigValue: string;
}

const Navbar = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showImpersonationDialog, setShowImpersonationDialog] = useState(false);
  const [showStopImpersonationDialog, setShowStopImpersonationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const { updateAuthContext } = useAuth();
  const canImpersonate = hasImpersonationFunctionality();
  const alreadyImpersonated = localStorage.getItem('impersonatedBy');
  const env = process.env.ENVIRONMENT;
  const baseUrl = process.env.REACT_APP_API_URL;
  const [systemConfig, setSystemConfig] = useState<SystemConfig[]>([]);
  let va_builder_name = systemConfig.find(item => item.Id === 'VA_builder_name')?.ConfigValue ;
  const navigate = useNavigate(); 
    

  useEffect(() => {
        const getCachedData = (key: string) => {
          const item = sessionStorage.getItem(key);
          if (!item) return null;
          const { value, timestamp } = JSON.parse(item);
          if (Date.now() - timestamp >2 * 24 * 60 * 60 * 1000) { // 30 minutes cache
            sessionStorage.removeItem(key);
            return null;
          }
          return value;
        };
  
        const config = getCachedData('systemConfig');
        if (config) {
          setSystemConfig(config);
        }
      }, []);

  const handleLogout = () => {
    setShowLogoutDialog(true);
    setIsPanelOpen(false);
  };

  const handleImpersonateClick = () => {
    setShowImpersonationDialog(true);
    setIsPanelOpen(false);
  };

  const handleStopImpersonation = async () => {
    try {
      setIsLoading(true);
      setLoadingMessage('Stopping impersonation...');
      localStorage.removeItem('impersonatedBy');
      localStorage.removeItem('hasImpersonated');
      
      const currentNetId = localStorage.removeItem('impersonatedBy');

      const response = await fetch(`${baseUrl}/api/auth/roles`, {
        credentials: 'include'
      });

      if (!response.ok) {
        setIsLoading(false);
        throw new Error('Impersonation failed');
      }

      const data = await response.json();

      if (data.user.FullName) {
        localStorage.setItem('fullName', data.user.FullName);
        localStorage.setItem('netId', data.data.NetID);
      }

      localStorage.setItem('roleFunctionalities', JSON.stringify(data.data.RoleFunctionalities));

      setCachedData('userRoles', data.data);
      setShowStopImpersonationDialog(false);

      await updateAuthContext();
      setIsLoading(false);
      navigate('/');
      window.location.reload();
    } catch (error) {
      setIsLoading(false);
      console.error('Stop impersonation failed:', error);
    }
  };
  
  const handleStopImpersonationClick = () => {
    setShowStopImpersonationDialog(true);
    setIsPanelOpen(false);
  };

  const setCachedData = (key: string, value: any) => {
    const data = {
      value,
      timestamp: Date.now()
    };
    sessionStorage.setItem(key, JSON.stringify(data));
  };

  const handleImpersonate = async (netId: string) => {
    try {
      setIsLoading(true);
      setLoadingMessage('Processing impersonation...');
      const currentNetId = localStorage.getItem('netId');
      setShowImpersonationDialog(false);

      const response = await fetch(`${baseUrl}/api/auth/impersonateduser`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestedNetId: netId,
          currentNetId: currentNetId
        })
      });

      if (!response.ok) {
        setIsLoading(false);
        throw new Error('Impersonation failed');
      }

      const data = await response.json();

      if (data.user.FullName) {
        localStorage.setItem('fullName', data.user.FullName);
        localStorage.setItem('netId', data.data.NetID);
      }

      localStorage.setItem('roleFunctionalities', JSON.stringify(data.data.RoleFunctionalities));
      localStorage.setItem('impersonatedBy', data.impersonatedBy);
      localStorage.setItem('hasImpersonated', 'yes');

      setCachedData('userRoles', data.data);
      setShowImpersonationDialog(false);

      await updateAuthContext();

      setIsLoading(false);
      navigate('/');
      window.location.reload();
    } catch (error) {
      setIsLoading(false);
      console.error('Impersonation failed:', error);
    }
  };

  const getUserName = () => {
    return localStorage.getItem('fullName');
  };

  const getUserEmail = () => {
    return localStorage.getItem('netId') + '@ads.northwestern.edu';
  };

  const isAlreadyImpersonated = () => {
    return !!localStorage.getItem('impersonatedBy');
  };

  const LOGOUT_URL = process.env.REACT_APP_LOGOUT_URL || 'https://www4dev.kellogg.northwestern.edu/WebSSOExternalAuthSite/Logout.aspx';

  const confirmLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = LOGOUT_URL;
    setShowLogoutDialog(false);
  };

  const profile = "/images/profileicon.svg";

  return (
    <>
      {/* Main Navbar */}
      <nav className="fixed top-0 left-0 w-full bg-white shadow-md z-40">
        <div className="container mx-auto">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-48 logo">
              <a href="/" className="text-2xl font-bold text-blue-500">
              </a>
              <span className="text-xl font-semibold text-gray-700">{va_builder_name}</span>
            </div>

            <div className="flex items-center gap-2 mr-4">
              {/* Notification Bell */}
              <NotificationBell userNetId={localStorage.getItem('netId') || ''} />

              {/* Profile Button */}
              <button
                onClick={() => setIsPanelOpen(true)}
                className="focus:outline-none"
              >
                <img
                  src={profile}
                  alt="User"
                  className="h-10 w-10 rounded-full"
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Slide-in Panel */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 z-50 ${
          isPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsPanelOpen(false)}
      >
        <div
          className={`absolute right-0 top-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
            isPanelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold">User Profile</h2>
            <button
              onClick={() => setIsPanelOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="p-6">
            <div className="flex items-center space-x-4 mb-8">
              <img
                src={profile}
                alt="User"
                className="h-16 w-16 rounded-full"
              />
              <div>
                <h3 className="text-lg font-medium">{getUserName()}</h3>
                <p className="text-sm text-gray-500">{getUserEmail()}</p>
              </div>
            </div>

            <div className="space-y-4">
              {(canImpersonate && !isAlreadyImpersonated()) && (
                <button
                  onClick={handleImpersonateClick}
                  className="flex items-center w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <FontAwesomeIcon icon={faUser} className="w-5 h-5 mr-3" />
                  <span>Impersonate User</span>
                </button>
              )}

              {isAlreadyImpersonated() && (
                <button
                  onClick={handleStopImpersonationClick}
                  className="flex items-center w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  <FontAwesomeIcon icon={faUserXmark} className="w-5 h-5 mr-3" />
                  <span>Stop Impersonation</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <FontAwesomeIcon icon={faPowerOff} className="w-5 h-5 mr-3" />
                <span>Log off</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-30 backdrop-blur-sm z-[100] pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-auto">
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="text-blue-500 text-4xl animate-spin mb-4" 
            />
            <div className="bg-white bg-opacity-90 px-6 py-3 rounded-lg shadow-lg">
              <p className="text-gray-700 text-lg">{loadingMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Logout Dialog */}
      {showLogoutDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold mb-4">Sign out</h2>
            <p className="text-gray-600 mb-6">Do you want to sign out?</p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowLogoutDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                onClick={confirmLogout}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Impersonation Dialogs */}
      <ImpersonationDialog
        isOpen={showImpersonationDialog}
        onClose={() => setShowImpersonationDialog(false)}
        onImpersonate={handleImpersonate}
      />
      
      <StopImpersonationDialog
        isOpen={showStopImpersonationDialog}
        onClose={() => setShowStopImpersonationDialog(false)}
        onConfirm={handleStopImpersonation}
      />
    </>
  );
};

export default Navbar;